import * as React from 'react';
import moment from 'moment';
import GenericHoc from 'src/components/hoc/GenericHoc';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import { vgmStyles } from './document-request.styles';
import { StylesProps } from 'src/Library/Types';
import { Collapse, Icon, Button, Spin, message, Tooltip } from 'antd';
import { getContainerStatus, RequestStatus, statusColorMap } from './document-request.constants';
import { bindActionCreators } from 'redux';
import { fetchAllSEDocuments, fetchVGMRequestDetails } from 'src/actions/shipmentExecutionActions';
import { isValidArray, isValidNumber, isValidString, stops } from 'src/Library/Helper';
import { ExecutionDetails } from '../SEDetails/shipment-details.types';
import { nl2Br } from 'src/utils/DOMUtils';
import { deleteVGMRequest, fetchVGMRequestStatus } from 'src/api/shipmentExecution';
import { useGenericState } from 'src/Library/UseGenericState';
import SEDeleteOdexDraftModal from './SEDeleteOdexDraftModal';
import Loader from 'src/components/common/Loader';

interface StateProps {
  isVGMRequestDeleting?: boolean;
  isDeleteVGMRequestModalVisible?: boolean;
  isContainerStatusRefreshing?: boolean;
}

interface VGMProps extends StylesProps<typeof vgmStyles> {
  userType;
  vgmRequests;
  cargoTypeList;
  containerList;
  vgmRequestData;
  selectedShipment;
  pendingContainers;
  updatedContainerStatus;
  actionsEnabled: boolean;
  isFetchingShippingData: boolean;
  fetchAllSEDocuments: (id) => void;
  fetchVGMRequestDetails: (id) => void;
  setFileVGMModalStates: (props) => void;
  shipmentDetails: ExecutionDetails | undefined;
  updateContainerStatus: (containerDetails) => void;
}

const VGMDocs = (props: VGMProps) => {
  const {
    classes,
    userType,
    vgmRequests,
    cargoTypeList,
    containerList,
    actionsEnabled,
    shipmentDetails,
    selectedShipment,
    pendingContainers,
    fetchAllSEDocuments,
    updateContainerStatus,
    setFileVGMModalStates,
    isFetchingShippingData,
    fetchVGMRequestDetails,
    updatedContainerStatus,
    vgmRequestData,
  } = props;

  const [active, setActive] = React.useState(0);
  const [state, setState] = useGenericState<StateProps>({});
  const {
    isVGMRequestDeleting,
    isDeleteVGMRequestModalVisible,
    isContainerStatusRefreshing,
  } = state;

  React.useEffect(() => {
    const vgmRequestId = vgmRequests?.[active]?.id;
    if (!selectedShipment?.id || !vgmRequestId || (!active && active !== 0)) {
      return;
    }
    fetchVGMRequestDetails(vgmRequestId);
  }, [active, selectedShipment]);

  const renderVgmList = () => {
    const isNewRequestDisabled = !actionsEnabled || pendingContainers <= 0;
    return <div className={classes.docList}>
      {vgmRequests?.map((item, index) => <div
        key={index}
        onClick={() => setActive(index)}
        className={classes.docCard}
        style={{
          color: active === index ? '#FFFFFF' : 'inherit',
          backgroundColor: active === index ? '#444444' : 'inherit',
          fontWeight: active === index ? 'bold' : 'inherit',
        }}
      >
        <div className={classes.docName}>
          {`VGM Request ${index + 1}`}
        </div>
        <div className={classes.docDate}>
          {item.createdAt && moment(item.createdAt).format('DD MMM YYYY | hh:mm A')}
        </div>
      </div>)}
      <div
        className={classes.addDocument}
        onClick={() => {
          if (isNewRequestDisabled) {
            return;
          }
          setFileVGMModalStates({ isFileVgmModalVisible: true });
        }}
        style={isNewRequestDisabled ?
          { cursor: 'not-allowed', color: 'lightgrey' } :
          undefined
        }
      >
      + New Request
      </div>
    </div>;
  };

  const renderDownload = () => {
    if (vgmRequestData?.containerWeighmentDetails?.some(container =>
      container.status === RequestStatus.VERIFIED)) {
      return (
        <Tooltip title="This will redirect to Odex to download the necessary files." placement="left">
          <a href={'https://odex.co/loadLanding.do'} target="_blank" rel="noopener noreferrer"
            style={{ color: '#424242' }}>
            <Icon type="download" />
          </a>
        </Tooltip>
      );
    }

    return;
  }

  const renderEditAndDelete = () => {
    if (!vgmRequestData?.isDraft || !actionsEnabled) {
      return;
    }

    return (
      <>
        <Icon
          type="edit"
          style={{ marginLeft: 8, cursor: pendingContainers <= 0 ? 'not-allowed' : 'pointer' }}
          theme="filled"
          onClick={() => {
            if (pendingContainers <= 0) return;
            setFileVGMModalStates({ isFileVgmModalVisible: true })
          }}
        />
        {isVGMRequestDeleting ? <Spin /> :
          <Icon
            type="delete"
            theme="filled"
            style={{ marginLeft: 8 }}
            onClick={() => setState({ isDeleteVGMRequestModalVisible: true })}
          />}
      </>
    );
  } 

  const renderReference = () => {
    return (
      <div className={classes.reference}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className={classes.referenceList}>
            <div className={classes.referenceValue}>{vgmRequestData?.referenceNumber || '-'}</div>
            <div className={classes.referenceTitle}>VGM Request #</div>
          </div>
          <div className={classes.referenceList}>
          {vgmRequestData?.isDraft &&
            <span className={classes.flexRow} style={{ marginLeft: '4px' }}>
              <strong>|</strong> <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 16,
                  backgroundColor: '#666666',
                  margin: '0 4px',
                }}
              /> Draft
            </span>}
          </div>
        </div>
        <div style={{ fontSize: 20 }}>
          {renderDownload()}
          {renderEditAndDelete()}
        </div>
      </div>
    );
  };

  const renderTitle = (title: string) => {
    return <div 
      className={classes.title}>
      {title}
    </div>;
  };
  const renderValue = (value: string | number | undefined, isDate: boolean = false) => {
    if (!value || value === 'N.A.') {
      return <div
        className={classes.na}>
          NA
      </div>;
    }
    if (isDate) {
      return <div
        className={classes.value}>
        {moment(value).format('DD/MM/YYYY | HH:mm A')}
      </div>;
    }
    return <div className={classes.value}>{value}</div>;
  };

  const renderField = (title: string, value: string | number | undefined, isDate: boolean = false) => {
    return <div
      className={classes.flexRow}
      style={{ width: '33%', alignItems: 'normal' }}>
      {renderTitle(title)} {renderValue(value, isDate)}
    </div>;
  };

  const renderContentTitle = (title: string) => {
    return <div 
      className={classes.contentTitle}>
      {title}
    </div>;
  };

  const renderLine = () => {
    return <div className={classes.line}/>;
  };

  const getPOL = () => {
    return vgmRequestData?.pol ? `${vgmRequestData?.pol?.name} (${vgmRequestData?.pol?.code})` : '-';
  }

  const renderShipperBooking = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Shipper / Booking Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('SL', vgmRequestData?.carrier?.name || '-')}
        {renderField('Vessel', vgmRequestData?.vessel || '-')}
        {renderField('POL', getPOL())}
      </div>
      <div className={classes.contentRow}>
        {renderField('Booking No.', vgmRequestData?.bookingNumber || '-')}
        {renderField('Voyage', vgmRequestData?.voyage || '-')}
      </div>
    </div>;
  };

  const renderAuthorised = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Authorized Person')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Name', vgmRequestData?.authorisedPerson?.name || '-')}
        {renderField('Phone', vgmRequestData?.authorisedPerson?.mobileno || '-')}
        {renderField('Designation', vgmRequestData?.authorisedPerson?.designation || '-')}
      </div>
    </div>;
  };

  const handleRetryError = (retryRequestData) : void => {
    setFileVGMModalStates({
      retryRequestData,
      isFileVgmModalVisible: true,
      isRetryError: true,
    });
  }

  const renderAction = (item) => {
    if (!actionsEnabled) {
      return;
    }

    switch (item?.status) {
      case RequestStatus.REQUESTED:
      case RequestStatus.PENDING: return (
        isContainerStatusRefreshing ? <Spin /> :
        <Icon
          type="reload"
          style={{ fontSize: 18 }}
          onClick={(e) => {
            stops(e);
            refreshContainerStatus(item?.containerNumber);
          }}
        />
      )
      case RequestStatus.REJECTED: return (
        <Button
          type="link"
          className={classes.button}
          onClick={() => handleRetryError({
            ...vgmRequestData,
            containerWeighmentDetails: [item],
          })}
        >
          Submit A New Request
        </Button>
      )
      default: return null;
    }
  };

  const refreshContainerStatus = async (containerNumber) => {
    setState({ isContainerStatusRefreshing: true });
    const response = await fetchVGMRequestStatus({
      containerNumber,
    }, vgmRequestData?.id)
    if (!response.isSuccess) {
      message.error(response.errorMessage)
    } else {
      updateContainerStatus(isSingleContainer ?
        {
          [`${vgmRequestData?.id}`]: response?.data?.containerStatus
        }
        :
        {
          [`${containerNumber}`]: response?.data?.containerStatus
        }
      );
    }
    setState({ isContainerStatusRefreshing: false });
  }

  const renderStatus = (item) => {
    const updatedStatus = isSingleContainer ? updatedContainerStatus?.[vgmRequestData?.id] :
      updatedContainerStatus?.[item?.containerNumber];
    const { title, ...statusStyles } = getContainerStatus(
      updatedStatus || item?.status
    );
    return <div
      className={classes.status}
      style={statusStyles}
    >
      {title}
    </div>;
  };

  const renderCollapseHeader = ({
    title,
    item,
  }: {
    title: string;
    item;
    }) => {
    
    return <div
      className={classes.flexRow}
      style={{ justifyContent: 'space-between' }}
    >
      <div className={classes.flexRow}>
        <div className={classes.containerTitle}>{title}</div>
        {renderStatus(item)}
      </div>
      {renderAction(item)}
    </div>;
  };

  const getContainerType = (item) => {
    const containerMaster = containerList?.find((container) => {
      return container.key.type === item;
    });
    return containerMaster?.label?.type;
  }

  const getCargoType = (key) => {
    return cargoTypeList[key];
  }

  const renderContent = (item) => {
    return <React.Fragment>
      <div className={classes.contentBox}>
        <div className={classes.contentRow}>
          {renderField('Type & Size', `${getContainerType(item.containerType) || '-'} | ${item.containerSize || '-'}`)}
          {renderField('Cargo Type', item.cargoType ? getCargoType(item.cargoType) : '-')}
        </div>
      </div>
      <div className={classes.contentBox}>
        {renderContentTitle('Weighment Details 1')}
        <div className={classes.contentRow}>
          {renderField('Weighbridge Reg. No.', item.weighBridgeRegistrationNumber || '-')}
          {renderField('Weighbridge Slip No.', item.weighBridgeSlipNumber || '-')}
        </div>
        <div className={classes.contentRow}>
          {renderField('Date & Time of Weighing', item.weighingTimestamp, true)}
          {renderField('CSC Plate Max. Wt. Limit', isValidNumber(item.cscPlateMaxWeightLimit?.value) ?
                  `${item.cscPlateMaxWeightLimit?.value} ${item.cscPlateMaxWeightLimit?.unit}` : '-')}
          {renderField('VGM', isValidNumber(item.weight?.value) ?
                  `${item.weight?.value} ${item.weight?.unit}` : '-')}
        </div>
        <div className={classes.contentRow}>
          {renderField('Address', isValidString(item?.weighBridgeAddress) ?
                    nl2Br(item?.weighBridgeAddress, '\n') as unknown as string
                  : '-')}
        </div>
      </div>
    </React.Fragment>;
  };

  const isSingleContainer = vgmRequestData?.containerWeighmentDetails?.length === 1;

  const renderContainerList = () => {
    const { containerWeighmentDetails } = vgmRequestData || {};
    return containerWeighmentDetails.map((item) => <Collapse
      className={classes.collapse}
    >
      <Collapse.Panel
        header={renderCollapseHeader({
          title: `Container ${item.containerNumber || "-"}`,
          item,
        })}
        key={item}
      >
        {renderContent(item)}
      </Collapse.Panel>
    </Collapse>);
  };

  const renderWeightbridge = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Container & Weighbridge Details')}
      {renderLine()}
      {renderContainerList()}
    </div>;
  };

  const getAuthLetter = () => {
    return (vgmRequestData?.shipperAuthLetterMapping?.documentUrl &&
      vgmRequestData?.shipperAuthLetterMapping?.documentName) ?
      <a href={vgmRequestData?.shipperAuthLetterMapping?.documentUrl}>
        {vgmRequestData?.shipperAuthLetterMapping?.documentName}
      </a>
      : '-';
  }

  const renderShipperDetails = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Shipper')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Name', shipmentDetails?.shipperName || '-')}
        {renderField('Authorization Letter by Shipper', getAuthLetter() as string)}
      </div>
    </div>;
  }

  const renderDeleteModal = () : React.ReactElement => {
    return (
      <SEDeleteOdexDraftModal
        isVisible={isDeleteVGMRequestModalVisible}
        onCancel={() => setState({ isDeleteVGMRequestModalVisible: false })}
        handleDelete={handleDelete}
        isRequestDeleting={isVGMRequestDeleting}
      />
    );
  }

  const handleDelete = async () : Promise<void> => {
    setState({ isVGMRequestDeleting: true });
    const response = await deleteVGMRequest(vgmRequestData?.id);
    if (response.isSuccess) {
      message.success('VGM Request Deleted Successfully');
      fetchAllSEDocuments(shipmentDetails?.id);
      setState({ isVGMRequestDeleting: false, isDeleteVGMRequestModalVisible: false });
    } else {
      message.error(response.errorMessage);
      setState({ isVGMRequestDeleting: false });
    }
  }

  if (isFetchingShippingData) {
    return <Loader
      style={{
        marginLeft: '18%',
        marginTop: '6%',
      }}
      zIndex={1}
    />;
  }
  return <div className={classes.main}>
    {renderVgmList()}
    {!!vgmRequests?.length && (
      <>
        {renderReference()}
        {renderShipperBooking()}
        {userType !== 'SHIPPER' && renderShipperDetails()}
        {renderAuthorised()}
        {isValidArray(vgmRequestData?.containerWeighmentDetails) && renderWeightbridge()}
        {isDeleteVGMRequestModalVisible && renderDeleteModal()}
      </>
    )}
  </div>;
};

const mapStateToProps = ({ shipmentExecutionReducer, masterData }, ownProps) => ({
  userType: masterData.userData.type,
  containerList: masterData.containerList,
  cargoTypeList: masterData.execution?.cargoType,
  vgmRequests: shipmentExecutionReducer.vgmRequests,
  vgmRequestData: shipmentExecutionReducer.vgmRequestData,
  pendingContainers: shipmentExecutionReducer.siPendingCount?.VGM?.pendingContainerCount,
  isFetchingShippingData: shipmentExecutionReducer.isFetchingShippingData,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  fetchAllSEDocuments,
  fetchVGMRequestDetails,
}, dispatch));

const options: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapDispatchToProps,
    mapStateToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: vgmStyles,
  },
};

const VGM = GenericHoc(options)(VGMDocs);
export default VGM;
